import styled from "styled-components";

import { AnnotationIcon, EyeIcon, FrameIcon, RotateCcwIcon, TrashIcon } from "../../icons/glyphs";
import { TooltipIconButton } from "../IconButton";
import { formatMediaPlaybackTime } from "../MediaTimelineBar";
import { NotificationBadge } from "../NotificationBadge";
import { Spinner } from "../Spinner";
import { Tooltip } from "../Tooltip";
import { TruncatedText } from "../TruncatedText";
import {
  CompositeListActionGroup,
  CompositeListActionSlot,
  CompositeListPrimaryButton,
  CompositeListRow,
} from "../foundations/compositeList";
import { FrameAnnotationGrid } from "./FrameAnnotationGrid";
import { hasAnnotationTargetContent } from "./annotationTargetContent";
import { annotationStudioText, capturedFrameAnnotationSummary } from "./messages";
import type {
  AnnotationFrameDraft,
  AnnotationFrameSummary,
  AnnotationSequence,
  AnnotationStream,
  AnnotationStudioLocale,
  AnnotationStudioViewMode,
  AnnotationTarget,
} from "./types";

const Row = styled(CompositeListRow).attrs({ $size: "regular" as const })`
  grid-template-columns: minmax(0, 1fr) auto;

  /* A preview kept outside the current camera/episode results is the same contextual state as
     ListView's off-filter pinned row, so it uses the shared attention ladder independently of
     selection. */
  &[data-outside-frame-filter] {
    --c6-composite-list-row-background: ${({ theme }) => theme.color.bg.attention};
  }

  &[data-outside-frame-filter]:hover,
  &[data-outside-frame-filter][data-selected="true"] {
    --c6-composite-list-row-background: ${({ theme }) => theme.color.bg.attentionHover};
  }

  &[data-outside-frame-filter][data-selected="true"]:hover,
  &[data-outside-frame-filter]:has([data-studio-list-primary]:active) {
    --c6-composite-list-row-background: ${({ theme }) => theme.color.bg.attentionActive};
  }
`;

const RowActions = styled(CompositeListActionGroup)``;

const FrameActionSlot = styled(CompositeListActionSlot).attrs({
  $size: "regular" as const,
})`
  & [data-frame-preview][aria-pressed="true"]:enabled {
    background-color: ${({ theme }) => theme.color.bg.selected};
  }

  & [data-frame-preview][aria-pressed="true"]:hover:enabled {
    background-color: ${({ theme }) => theme.color.bg.selectedHover};
  }

  & [data-frame-preview][aria-pressed="true"]:active:enabled {
    background-color: ${({ theme }) => theme.color.bg.selectedActive};
  }
`;

const AnnotationStatusTooltip = styled(Tooltip)`
  width: 2rem;
  height: 100%;
  min-height: 0;
  align-items: stretch;
  grid-column: 3;
  grid-row: 1 / span 2;
`;

const AnnotationStatusLabel = styled.span`
  --c6-frame-annotation-empty-background: ${({ theme }) => theme.color.bg.surfaceSunken};

  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  align-items: center;
  justify-content: center;
`;

const FrameButton = styled(CompositeListPrimaryButton).attrs({
  $size: "regular" as const,
})<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 1rem minmax(0, 1fr) 2rem;
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: 0.55rem;
  row-gap: 0.05rem;
  padding: 0.38rem 0.55rem;
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ $selected, theme }) =>
    $selected ? theme.typography.weight.bold : theme.typography.weight.medium};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const FrameIconBadge = styled(NotificationBadge)<{ $annotated: boolean }>`
  grid-column: 1;
  grid-row: 1 / span 2;
  align-self: center;
  justify-self: center;
  color: ${({ $annotated, theme }) =>
    $annotated ? theme.color.text.default : theme.color.text.subtle};
  font-size: 1rem;

  ${FrameButton}:disabled & {
    color: ${({ theme }) => theme.color.text.disabled};
  }
`;

const FrameIconSurface = styled.span`
  display: inline-flex;
  width: 1em;
  height: 1em;
`;

const FrameLoading = styled.span`
  display: inline-flex;
  grid-column: 1;
  grid-row: 1 / span 2;
  align-self: center;
  justify-self: center;
  color: ${({ theme }) => theme.color.text.subtle};
`;

const FrameLoadRetry = styled.span`
  display: inline-flex;
  grid-column: 1;
  grid-row: 1 / span 2;
  align-self: center;
  justify-self: center;
  color: ${({ theme }) => theme.color.text.warning};
  font-size: 1rem;
`;

const FrameTime = styled.span`
  min-width: 0;
  grid-column: 2;
  grid-row: 1;
  color: inherit;

  ${FrameButton}:disabled & {
    color: ${({ theme }) => theme.color.text.disabled};
  }
`;

const FrameEpisode = styled(TruncatedText)`
  width: 100%;
  min-width: 0;
  grid-column: 2;
  grid-row: 2;
  color: ${({ theme }) => theme.color.text.subtle};
  font-weight: ${({ theme }) => theme.typography.weight.regular};

  ${FrameButton}:disabled & {
    color: ${({ theme }) => theme.color.text.disabled};
  }
`;

export function CapturedFrameListItem({
  locale,
  viewMode,
  sequences,
  streams,
  targets,
  frame,
  contextual,
  outsideFilter,
  active,
  selected,
  previewed,
  materializing = false,
  materializationFailed = false,
  hasSavedAnnotation,
  dirty,
  saveConflict,
  needsAttention,
  authoring,
  removeAvailable,
  removeDisabled,
  onSelect,
  onTogglePreview,
  onRequestRemove,
}: {
  locale: AnnotationStudioLocale;
  viewMode: AnnotationStudioViewMode;
  sequences: readonly AnnotationSequence[];
  streams: readonly AnnotationStream[];
  targets: readonly AnnotationTarget[];
  frame: AnnotationFrameDraft | AnnotationFrameSummary;
  contextual: boolean;
  outsideFilter: boolean;
  active: boolean;
  selected: boolean;
  previewed: boolean;
  materializing?: boolean;
  materializationFailed?: boolean;
  hasSavedAnnotation: boolean;
  dirty: boolean;
  saveConflict: boolean;
  needsAttention: boolean;
  authoring: boolean;
  removeAvailable: boolean;
  removeDisabled: boolean;
  onSelect: () => void;
  onTogglePreview: () => void;
  onRequestRemove: () => void;
}) {
  const sequence =
    sequences.find((candidate) => candidate.id === frame.sequenceId)?.label ?? frame.sequenceId;
  const stream =
    streams.find((candidate) => candidate.id === frame.streamId)?.label ?? frame.streamId;
  const time = formatMediaPlaybackTime(frame.timestampSeconds);
  const identity = `${sequence} · ${time}`;
  const episodeContext = outsideFilter ? `${sequence} · ${stream}` : sequence;
  const annotatedTargetIds = new Set(
    "targets" in frame
      ? targets.flatMap((target) => {
          const draft = frame.targets[target.id];
          return draft && hasAnnotationTargetContent(draft) ? [target.id] : [];
        })
      : frame.annotatedTargetIds,
  );
  const hasAnnotation = annotatedTargetIds.size > 0;
  const editing = viewMode === "annotate" && active;
  const annotationSummary = capturedFrameAnnotationSummary(locale, annotatedTargetIds.size);
  const frameAction =
    viewMode === "browse"
      ? annotationStudioText(authoring ? "editAnnotation" : "viewAnnotation", locale)
      : annotationStudioText("selectCapturedFrame", locale);
  const captureLabel = `${frameAction}: ${identity} · ${stream} · ${annotationStudioText(
    hasAnnotation ? "occupiedAnnotation" : "emptyAnnotation",
    locale,
  )}${contextual ? ` · ${annotationStudioText("frameInSelectedEpisode", locale)}` : ""}${
    outsideFilter ? ` · ${annotationStudioText("outsideCurrentFilter", locale)}` : ""
  }${dirty ? ` · ${annotationStudioText("unsaved", locale)}` : ""}${
    saveConflict ? ` · ${annotationStudioText("saveConflict", locale)}` : ""
  }${needsAttention ? ` · ${annotationStudioText("needsAttention", locale)}` : ""}`;
  const materializationFailureLabel = materializationFailed
    ? ` · ${annotationStudioText("retryCapturedFrameLoad", locale)}`
    : "";
  const removeAction = !authoring ? null : (
    <TooltipIconButton
      data-frame-remove=""
      data-studio-list-danger=""
      size="xs"
      variant="ghost"
      tone="danger"
      tooltip={{
        content: annotationStudioText("removeCapturedFrame", locale),
        placement: "bottom",
      }}
      aria-label={`${annotationStudioText("removeCapturedFrame", locale)}: ${identity}`}
      disabled={!removeAvailable || removeDisabled || materializing}
      onClick={onRequestRemove}
    >
      <TrashIcon />
    </TooltipIconButton>
  );
  const previewAction = (
    <TooltipIconButton
      data-frame-preview=""
      data-frame-preview-current={previewed ? "" : undefined}
      size="xs"
      variant="ghost"
      border={false}
      selected={previewed}
      tooltip={{
        content: annotationStudioText("framePreview", locale),
        placement: "bottom",
      }}
      aria-label={`${annotationStudioText("previewCapturedFrame", locale)}: ${identity}`}
      disabled={materializing}
      onClick={onTogglePreview}
    >
      <EyeIcon data-frame-preview-icon="show" />
    </TooltipIconButton>
  );

  return (
    <Row
      data-captured-frame-row=""
      data-frame-id={frame.frameId}
      $current={active}
      $selected={selected}
      $contextual={contextual}
      data-editing={editing}
      data-selected={selected}
      data-previewed={previewed}
      data-captured-frame-materialization-failed={materializationFailed || undefined}
      data-selected-episode-frame={contextual ? "" : undefined}
      data-outside-frame-filter={outsideFilter ? "" : undefined}
    >
      <FrameButton
        type="button"
        data-studio-list-primary=""
        data-captured-frame-select=""
        $selected={selected}
        aria-label={`${captureLabel}${materializationFailureLabel}${
          editing ? ` · ${annotationStudioText("editingFrame", locale)}` : ""
        }${previewed ? ` · ${annotationStudioText("currentlyPreviewing", locale)}` : ""}`}
        aria-current={active ? "true" : undefined}
        aria-pressed={selected}
        aria-busy={materializing || undefined}
        disabled={materializing}
        onClick={onSelect}
      >
        {materializing ? (
          <FrameLoading data-captured-frame-materializing="">
            <Spinner label={annotationStudioText("loadingCapturedFrame", locale)} size="sm" />
          </FrameLoading>
        ) : materializationFailed ? (
          <FrameLoadRetry data-captured-frame-materialization-retry="">
            <RotateCcwIcon aria-hidden="true" />
          </FrameLoadRetry>
        ) : (
          <FrameIconBadge
            $annotated={hasSavedAnnotation}
            visible={dirty || saveConflict || needsAttention}
            tone={saveConflict || needsAttention ? "error" : "warning"}
            size="0.45rem"
            offset="-0.18rem"
          >
            <FrameIconSurface
              data-captured-frame-icon={hasSavedAnnotation ? "annotated" : "empty"}
              data-captured-frame-dirty={dirty ? "" : undefined}
              data-captured-frame-save-conflict={saveConflict ? "" : undefined}
              data-captured-frame-validation-error={needsAttention ? "" : undefined}
              data-captured-frame-indicator-tone={
                saveConflict || needsAttention ? "error" : dirty ? "warning" : undefined
              }
            >
              {hasSavedAnnotation ? (
                <AnnotationIcon aria-hidden="true" />
              ) : (
                <FrameIcon aria-hidden="true" />
              )}
            </FrameIconSurface>
          </FrameIconBadge>
        )}
        <FrameTime data-captured-frame-time="">{time}</FrameTime>
        <FrameEpisode
          data-frame-context="list"
          data-captured-frame-episode=""
          fullText={episodeContext}
        >
          {episodeContext}
        </FrameEpisode>
        <AnnotationStatusTooltip
          content={annotationSummary}
          placement="top"
          describeTrigger={false}
        >
          <AnnotationStatusLabel
            role="img"
            aria-label={`${annotationSummary}: ${identity}`}
            data-frame-annotation-label=""
          >
            <FrameAnnotationGrid
              targets={targets}
              annotatedTargetIds={annotatedTargetIds}
              data-captured-frame-annotation-grid=""
              data-captured-frame-annotation={hasAnnotation ? "" : undefined}
            />
          </AnnotationStatusLabel>
        </AnnotationStatusTooltip>
      </FrameButton>
      <RowActions data-frame-row-actions="">
        <FrameActionSlot
          $visible={previewed}
          data-studio-list-action-slot=""
          data-visible={previewed ? "true" : "false"}
        >
          {previewAction}
        </FrameActionSlot>
        {authoring ? (
          <FrameActionSlot
            $visible={previewed}
            data-studio-list-action-slot=""
            data-visible={previewed ? "true" : "false"}
          >
            {removeAction}
          </FrameActionSlot>
        ) : null}
      </RowActions>
    </Row>
  );
}
